import { Fragment } from 'react'
import { useAtomValue } from 'jotai'
import { MessageSquareText } from 'lucide-react'

import { isAskUserFeedbackStagedAction } from '@renderer/chat/integrations/ask-user-feedback'
import { isAnswerableStagedAction, isPendingAnswerableStagedAction } from '@renderer/chat/integrations/answerable'
import { StagedActionMarkdown } from '@renderer/chat/integrations/staged-action'
import { isRegularChat } from '@renderer/overlay/hooks/running-chat-selection'
import { ActionHeader } from '@renderer/overlay/Sidepanel/components/action-header'
import { ConversationSkillIcon } from '@renderer/overlay/Sidepanel/components/conversation-skill-icon'
import { ActionViewFeedbackCard } from '@renderer/overlay/Sidepanel/views/action-view/nested/feedback-card'
import { answeredAnswerableOutputs, questionAssistantResponse, resolvedAnswerableOutputs } from '../chat-output'
import { optimisticQuestionAnswersAtom } from '../question-progress-state'
import type { useChatOutputViewModel } from '../use-chat-output-view-model'
import { OutputCaption, OutputMeetingSource } from './caption-pill'
import { ResolvedQuestionOutputs, StagedQuestionOutput } from './question-outputs'
import { StagedActionOutput } from './staged-action-output'

export function CompletedOutput({
  model,
  captionByActionId,
  focusedActionId,
  animateControlsHeight,
  actionHotkeysEnabled,
  onFocusAction,
  onOpenThread,
  threadOpen,
}: {
  model: ReturnType<typeof useChatOutputViewModel>
  captionByActionId: ReadonlyMap<string, string>
  focusedActionId: string | undefined
  animateControlsHeight: boolean
  actionHotkeysEnabled: boolean
  onFocusAction: (actionId: string) => void
  onOpenThread: () => void
  threadOpen: boolean
}) {
  const optimisticQuestionAnswers = useAtomValue(optimisticQuestionAnswersAtom)
  const hasOutput = !!model.assistantText || model.stagedActions.length > 0
  const hasPendingQuestion = model.stagedActions.some(isPendingAnswerableStagedAction)
  // Id list, not resolved actions. Standalone hides in-card controls on the
  // promise that the floating bar renders them instead, and that bar mounts
  // off the id list in view-registry.
  const hasSingleAction = model.stagedActionIds.length === 1
  // A lone card already carries the title, so the summary would only restate it
  // (a scheduled skill's title is "<skill> - <fired at>" over a card holding the
  // headline the skill wrote). Stand it down only once that card is on screen. An
  // id that never resolves against the staged-action query renders no card, and
  // the summary is what carries the error and empty copy.
  const showSummary = !hasSingleAction || model.stagedActions.length === 0
  const rows = model.stagedActions.map((action) => {
    // Feedback settles through the same answered rows as a question; only the
    // unanswered card differs, so the split happens below, not here.
    const isAnswerable = isAnswerableStagedAction(action)
    // Archived questions keep the compact archived treatment above;
    // their prior answers should not bring the hidden body cards back.
    const resolvedQuestions = isAnswerable && !action.archivedAt ? resolvedAnswerableOutputs(action) : null
    if (resolvedQuestions) {
      return { kind: 'resolved-questions' as const, action, questions: resolvedQuestions }
    }
    const optimisticQuestions =
      isAnswerable && !action.archivedAt ? answeredAnswerableOutputs(action, optimisticQuestionAnswers[action.id]) : []
    if (optimisticQuestions.length > 0) {
      return { kind: 'optimistic-questions' as const, action, questions: optimisticQuestions }
    }
    if (isAskUserFeedbackStagedAction(action) && !action.archivedAt && !hasPendingQuestion) {
      return { kind: 'feedback' as const, action }
    }
    if (isAnswerable && !action.archivedAt) {
      return { kind: 'question' as const, action }
    }
    return { kind: 'action' as const, action }
  })
  // The meeting pill needs a host. With no summary it rides the lone card's
  // header, except on a feedback card, which has none, so there it sits above.
  const loneRow = showSummary ? undefined : rows[0]
  const headerMeetingAudioNoteId = loneRow && loneRow.kind !== 'feedback' ? model.audioNoteId : undefined
  const looseMeetingAudioNoteId = loneRow?.kind === 'feedback' ? model.audioNoteId : undefined
  // The floating bar reports live turns; an empty status row would only shift the meeting pill.
  const summaryStatus = model.isStreaming ? null : model.isLoadingMessages ? (
    <p className="text-foreground-secondary type-ui-sm py-2">Loading message…</p>
  ) : model.isMessagesError ? (
    <p className="text-danger-subtle type-ui-sm py-2">Couldn’t load this chat’s message.</p>
  ) : !hasOutput ? (
    <p className="text-foreground-secondary type-ui-sm py-2">No output yet.</p>
  ) : null

  return (
    <div className="flex flex-col gap-2">
      {showSummary && (
        <>
          <ActionHeader
            icon={
              !isRegularChat(model) && model.skillId ? (
                <ConversationSkillIcon skillId={model.skillId} />
              ) : (
                <span
                  className="text-foreground-secondary flex size-5 shrink-0 items-center justify-center"
                  aria-hidden="true"
                >
                  <MessageSquareText className="size-4" />
                </span>
              )
            }
            title={model.title}
            annotation={model.audioNoteId ? <OutputMeetingSource audioNoteId={model.audioNoteId} /> : undefined}
            className="py-2"
          />
          {/* The chat's caption lives on each card it touched, not here. */}
          {summaryStatus && <div className="mt-1">{summaryStatus}</div>}
          {/* A scheduled skill that stopped in prose instead of a card still has something to read. */}
          {model.stagedActions.length === 0 && model.assistantText && (
            <StagedActionMarkdown>{model.assistantText}</StagedActionMarkdown>
          )}
        </>
      )}
      {looseMeetingAudioNoteId && (
        <div className="px-3 pb-2 pt-3">
          <OutputMeetingSource audioNoteId={looseMeetingAudioNoteId} />
        </div>
      )}
      {rows.length > 0 && (
        <div className={showSummary ? 'border-t py-3' : undefined}>
          {rows.map((row, index) => {
            const { action } = row
            const caption = captionByActionId.get(action.id)
            const nextAction = rows[index + 1]?.action
            const connectToNext = nextAction !== undefined && !isAskUserFeedbackStagedAction(nextAction)

            switch (row.kind) {
              case 'resolved-questions':
                return (
                  <ResolvedQuestionOutputs
                    key={action.id}
                    actionId={action.id}
                    questions={row.questions}
                    emptyAnswerLabel={isAskUserFeedbackStagedAction(action) ? 'Skipped' : undefined}
                    // Answered questions keep their dedicated response instead of the turn caption.
                    assistantResponse={questionAssistantResponse(action) ?? caption}
                    meetingAudioNoteId={headerMeetingAudioNoteId}
                    connectToNext={connectToNext}
                    threadOpen={threadOpen}
                    onOpenThread={onOpenThread}
                  />
                )
              case 'optimistic-questions':
                return (
                  <Fragment key={action.id}>
                    <ResolvedQuestionOutputs
                      actionId={action.id}
                      questions={row.questions}
                      emptyAnswerLabel={isAskUserFeedbackStagedAction(action) ? 'Skipped' : undefined}
                      meetingAudioNoteId={headerMeetingAudioNoteId}
                      connectToNext
                      threadOpen={threadOpen}
                      onOpenThread={onOpenThread}
                    />
                    <StagedQuestionOutput
                      actionId={action.id}
                      label={action.view?.title ?? action.title ?? 'Question'}
                      caption={caption}
                      focused={focusedActionId === action.id}
                      standalone={hasSingleAction}
                      connectToNext={connectToNext}
                      threadOpen={threadOpen}
                      onFocus={() => onFocusAction(action.id)}
                      onOpenThread={onOpenThread}
                    />
                  </Fragment>
                )
              case 'feedback':
                return (
                  <Fragment key={action.id}>
                    <div className="px-3">
                      <ActionViewFeedbackCard action={action} />
                    </div>
                    {caption && (
                      <OutputCaption
                        text={caption}
                        standalone={hasSingleAction}
                        threadOpen={threadOpen}
                        onOpenThread={onOpenThread}
                      />
                    )}
                  </Fragment>
                )
              case 'question':
                return (
                  <StagedQuestionOutput
                    key={action.id}
                    actionId={action.id}
                    label={action.view?.title ?? action.title ?? 'Question'}
                    caption={caption}
                    meetingAudioNoteId={headerMeetingAudioNoteId}
                    focused={focusedActionId === action.id}
                    standalone={hasSingleAction}
                    connectToNext={connectToNext}
                    threadOpen={threadOpen}
                    onFocus={() => onFocusAction(action.id)}
                    onOpenThread={onOpenThread}
                  />
                )
              case 'action':
                return (
                  <StagedActionOutput
                    key={action.id}
                    actionId={action.id}
                    label={action.view?.title ?? action.title ?? 'Action'}
                    caption={caption}
                    focused={focusedActionId === action.id}
                    animateHeight={animateControlsHeight}
                    hotkeysEnabled={actionHotkeysEnabled}
                    connectToNext={connectToNext}
                    standalone={hasSingleAction}
                    meetingAudioNoteId={headerMeetingAudioNoteId}
                    hideContent={hasPendingQuestion}
                    threadOpen={threadOpen}
                    onFocus={() => onFocusAction(action.id)}
                    onOpenThread={onOpenThread}
                  />
                )
            }
          })}
        </div>
      )}
    </div>
  )
}
